import { useMemo } from 'react';

import { AccessModes, Dimensions, Metrics, SortOrder, Sources } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import useInsightsAnalytics, { InsightsQueryParams } from 'components/insights/query/useInsightsAnalytics';
import { ReviewType } from 'models/Review';
import { getNetworkPerformanceAnalyticsFilters, useNetworkPerformanceStore } from 'stores/networkPerformanceStore';

// --- Query param configs ---

const DISCHARGE_PARAMS: InsightsQueryParams = {
  source: Sources.LOCATION_EPISODE_DISCHARGES,
  accessModes: [AccessModes.MANAGER],
  dimensions: [Dimensions.GROUP_NAME],
  metrics: [
    Metrics.ID_COUNT,
    Metrics.LENGTH_OF_STAY_AVERAGE,
    Metrics.READMISSION_RATE,
    Metrics.OLIO_ENGAGEMENT_SCORE,
    Metrics.ACTIVITIES_PER_PATIENT_PER_WEEK,
    Metrics.READMISSIONS_SUM,
  ],
  sortBy: `${Metrics.ID_COUNT} ${SortOrder.DESC}`,
  rollups: true,
};

const CARE_TRANSITIONS_PARAMS: InsightsQueryParams = {
  source: Sources.INSIGHTS_CARE_TRANSITIONS,
  accessModes: [AccessModes.MANAGER],
  dimensions: [Dimensions.GROUP],
  metrics: [Metrics.DAYS_TO_START_CARE_AVERAGE, Metrics.LOCATION_EPISODE_ID_COUNT],
  sortBy: `${Metrics.DAYS_TO_START_CARE_AVERAGE} ${SortOrder.DESC}`,
  rollups: true,
};

const SERVICE_REFUSALS_PARAMS: InsightsQueryParams = {
  source: Sources.REVIEWS,
  accessModes: [AccessModes.MANAGER],
  dimensions: [Dimensions.GROUP, Dimensions.SERVICE_REFUSAL_REFUSED_BY_TYPE],
  metrics: [Metrics.SERVICE_REFUSALS_COUNT],
  reviewType: ReviewType.SERVICE_REFUSAL,
  rollups: true,
};

const SAFE_RETURNS_PARAMS: InsightsQueryParams = {
  source: Sources.REVIEWS,
  accessModes: [AccessModes.MANAGER],
  dimensions: [Dimensions.GROUP],
  metrics: [Metrics.SAFE_RETURNS_REQUESTED_COUNT, Metrics.SAFE_RETURN_RESPONSE_TIME_AVERAGE],
  reviewType: ReviewType.SAFE_RETURN,
  rollups: true,
};

// --- Hook ---

export type EntitySlideoutAnalyticsResult = {
  dischargeRollups: AnalyticsResponseRollupCollection;
  careTransitionsRollups: AnalyticsResponseRollupCollection;
  serviceRefusalsRollups: AnalyticsResponseRollupCollection;
  safeReturnsRollups: AnalyticsResponseRollupCollection;
  loading: boolean;
};

export default function useEntitySlideoutAnalytics(
  entityId: string,
  entityName: string
): EntitySlideoutAnalyticsResult {
  const storeFilters = useNetworkPerformanceStore((state) => getNetworkPerformanceAnalyticsFilters(state));

  const filters = useMemo(() => {
    if (!storeFilters) return null;
    return { ...storeFilters, providerClient: [{ id: entityId, name: entityName }] };
  }, [storeFilters, entityId, entityName]);

  const discharges = useInsightsAnalytics(DISCHARGE_PARAMS, filters, {
    queryKey: `entitySlideout:discharges:${entityId}`,
  });

  const careTransitions = useInsightsAnalytics(CARE_TRANSITIONS_PARAMS, filters, {
    queryKey: `entitySlideout:careTransitions:${entityId}`,
  });

  const serviceRefusals = useInsightsAnalytics(SERVICE_REFUSALS_PARAMS, filters, {
    queryKey: `entitySlideout:serviceRefusals:${entityId}`,
  });

  const safeReturns = useInsightsAnalytics(SAFE_RETURNS_PARAMS, filters, {
    queryKey: `entitySlideout:safeReturns:${entityId}`,
  });

  return {
    dischargeRollups: discharges.rollups,
    careTransitionsRollups: careTransitions.rollups,
    serviceRefusalsRollups: serviceRefusals.rollups,
    safeReturnsRollups: safeReturns.rollups,
    loading: discharges.loading || careTransitions.loading || serviceRefusals.loading || safeReturns.loading,
  };
}
